import { api } from '../api/client';
import { ApiError } from '../api/types';
import { newClientId } from '../utils/ids';
import { asPromise, openDB, STORE_QUEUE, txStore } from './db';

export type QueueAction =
  | 'addTx'
  | 'updateTx'
  | 'deleteTx'
  | 'updateGoal'
  | 'updateCategory'
  | 'addGameEvent'
  | 'putReserve'
  | 'sweepReserve';
export type QueueStatus = 'pending' | 'failed';
export const FAILED_AFTER_ATTEMPTS = 3;

export interface QueueEntry {
  id: string;
  action: QueueAction;
  payload: Record<string, unknown>;
  createdAt: number;
  attempts: number;
  lastError?: string;
  status: QueueStatus;
}

export async function enqueue(
  action: QueueAction,
  payload: Record<string, unknown>,
): Promise<QueueEntry> {
  const entry: QueueEntry = {
    id: newClientId(),
    action,
    payload,
    createdAt: Date.now(),
    attempts: 0,
    status: 'pending',
  };
  const db = await openDB();
  await asPromise(txStore(db, STORE_QUEUE, 'readwrite').add(entry));
  return entry;
}

export async function listQueue(): Promise<QueueEntry[]> {
  try {
    const db = await openDB();
    const items = (await asPromise(
      txStore(db, STORE_QUEUE, 'readonly').getAll(),
    )) as QueueEntry[];
    items.sort((a, b) => a.createdAt - b.createdAt);
    return items;
  } catch {
    return [];
  }
}

async function removeEntry(id: string): Promise<void> {
  const db = await openDB();
  await asPromise(txStore(db, STORE_QUEUE, 'readwrite').delete(id));
}

async function saveEntry(entry: QueueEntry): Promise<void> {
  const db = await openDB();
  await asPromise(txStore(db, STORE_QUEUE, 'readwrite').put(entry));
}

export async function resetFailedAttempts(): Promise<void> {
  const all = await listQueue();
  for (const e of all) {
    if (e.status === 'failed') {
      e.attempts = 0;
      e.status = 'pending';
      e.lastError = undefined;
      await saveEntry(e);
    }
  }
}

export interface DrainResult {
  processed: number;
  /**
   * Записи, доехавшие до сервера в этом проходе (в порядке применения).
   * Нужны вызывающей стороне, чтобы влить их в `data`: из очереди они уже
   * удалены, а полного refresh после каждого слива больше нет (план 3b).
   */
  synced: QueueEntry[];
  remaining: number;
  failed: number;
  stoppedByNetwork: boolean;
  /** Истёкшая сессия: очередь сохранена, нужен re-login (не считаем попытки). */
  stoppedByAuth: boolean;
}

/**
 * Прогоняет очередь по порядку. Если сеть отвалилась — останавливается.
 * Бизнес-ошибки увеличивают attempts; после 3 неудач запись помечается failed
 * и пропускается до явного резета.
 */
export async function drainQueue(): Promise<DrainResult> {
  const items = await listQueue();
  const synced: QueueEntry[] = [];
  let stoppedByNetwork = false;
  let stoppedByAuth = false;

  for (const item of items) {
    if (item.status === 'failed') continue;

    try {
      await callApi(item.action, item.payload);
      await removeEntry(item.id);
      synced.push(item);
    } catch (e) {
      const isApi = e instanceof ApiError;
      const code = isApi ? e.code : 'internal';

      if (code === 'unauthorized') {
        // сессия истекла: стоп без attempts+1 — записи доедут после логина
        stoppedByAuth = true;
        break;
      }

      if (code === 'network' || code === 'internal') {
        // временная проблема — стоп, оставляем всё в очереди
        stoppedByNetwork = true;
        break;
      }

      if (code === 'not_found' && item.action === 'deleteTx') {
        // op уже отсутствует на сервере — считаем успехом синка
        await removeEntry(item.id);
        synced.push(item);
        continue;
      }

      // бизнес-ошибка: увеличиваем счётчик, помечаем failed после лимита
      item.attempts += 1;
      item.lastError = isApi ? e.message : String(e);
      if (item.attempts >= FAILED_AFTER_ATTEMPTS) {
        item.status = 'failed';
      }
      await saveEntry(item);
    }
  }

  const remaining = (await listQueue()).filter((e) => e.status === 'pending').length;
  const failed = (await listQueue()).filter((e) => e.status === 'failed').length;

  return { processed: synced.length, synced, remaining, failed, stoppedByNetwork, stoppedByAuth };
}

async function callApi(action: QueueAction, payload: Record<string, unknown>): Promise<unknown> {
  switch (action) {
    case 'addTx':           return api.addTx(payload as never);
    case 'updateTx':        return api.updateTx(payload as never);
    case 'deleteTx':        return api.deleteTx((payload as { id: string }).id);
    case 'updateGoal':      return api.updateGoal(payload as never);
    case 'updateCategory':  return api.updateCategory(payload as never);
    // сервер идемпотентен по id и singleton-ключу — ретраи безопасны
    case 'addGameEvent':    return api.addGameEvent(payload as never);
    // upsert по PK — идемпотентен
    case 'putReserve':      return api.putReserve(payload as never);
    // идемпотентен по клиентскому id взноса и state='swept'
    case 'sweepReserve':    return api.sweepReserve(payload as never);
  }
}
